import React, { useState, useMemo } from 'react';
import CreatableSelect from 'react-select/creatable';
import { components as selectComponents } from 'react-select';
import { SketchPicker } from 'react-color';
import CreateTagModal from './CreateTagModal';

// Opciones por defecto para etiquetas estándar con colores fijos
const defaultTagOptions = [
  { value: 'VIP', label: 'VIP', color: 'green' },
  { value: 'Frecuentes', label: 'Frecuentes', color: 'orange' },
  { value: 'Inactivos', label: 'Inactivos', color: 'red' },
  { value: 'Sin Compras', label: 'Sin Compras', color: 'gray' },
];

// Estilos personalizados para react-select
const customSelectStyles = {
  container: (provided) => ({ ...provided, width: '100%' }),
  multiValue: (styles, { data }) => ({
    ...styles,
    backgroundColor: data.color || '#777',
  }),
  multiValueLabel: (styles) => ({ ...styles, color: 'white' }),
  menu: (provided) => ({ ...provided, zIndex: 9999 }),
};

// Componente personalizado para MultiValue que permite editar el color al hacer clic
const MultiValue = (props) => {
  const { data } = props;
  const onLabelClick = (event) => {
    event.stopPropagation();
    if (props.selectProps.onEditColor) {
      props.selectProps.onEditColor(data);
    }
  };
  return (
    <selectComponents.MultiValue {...props}>
      <div onClick={onLabelClick} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
        <div
          style={{
            width: 10,
            height: 10,
            backgroundColor: data.color || '#777',
            borderRadius: '50%',
            marginRight: 4,
          }}
        ></div>
        <selectComponents.MultiValueLabel {...props} />
      </div>
    </selectComponents.MultiValue>
  );
};

const EditClienteModal = ({ cliente, onClose, onSave, tagOptions = defaultTagOptions }) => {
  // Aseguramos que geographic_info y segments tengan valores por defecto para evitar errores
  const initialCliente = {
    ...cliente,
    geographic_info: cliente.geographic_info || { country: '', state: '', city: '', postal_code: '' },
    genero: cliente.genero || 'PREFIERO_NO_DECIRLO', // Aseguramos que tenga un valor por defecto
    segments: Array.isArray(cliente.segments)
      ? cliente.segments.map((tag) =>
        typeof tag === 'object'
          ? tag
          : { value: tag, label: tag, color: '#777' }
      )
      : [],
  };

  const [editingCliente, setEditingCliente] = useState(initialCliente);
  const [colorPickerTag, setColorPickerTag] = useState(null);
  const [showCreateTagModal, setShowCreateTagModal] = useState(false);
  const [newTagDefaultName, setNewTagDefaultName] = useState('');

  // Calculamos las opciones disponibles para el select (sin duplicados, ignorando mayúsculas)
  const mergedTagOptions = useMemo(() => {
    const map = new Map();
    // Primero agregamos las opciones por defecto
    tagOptions.forEach((tag) => {
      map.set(tag.value.toLowerCase(), tag);
    });
    // Luego agregamos las etiquetas que ya tiene el cliente, sin duplicar
    editingCliente.segments.forEach((tag) => {
      if (!map.has(tag.value.toLowerCase())) {
        map.set(tag.value.toLowerCase(), tag);
      }
    });
    return Array.from(map.values());
  }, [tagOptions, editingCliente.segments]);

  // Manejador para inputs simples y anidados (por ejemplo, geographic_info)
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('geographic_info')) {
      const [group, key] = name.split('.');
      setEditingCliente((prev) => ({
        ...prev,
        [group]: { ...prev[group], [key]: value },
      }));
    } else {
      setEditingCliente((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Manejador para cambios en el CreatableSelect de etiquetas
  const handleSegmentsChange = (selectedOptions, actionMeta) => {
    if (actionMeta.action === 'create-option') {
      // Verificar si ya existe una etiqueta con ese nombre (sin duplicados)
      const exists = editingCliente.segments.some(
        (tag) => tag.value.toLowerCase() === actionMeta.option.value.toLowerCase()
      );
      if (exists) return;
      setNewTagDefaultName(actionMeta.option.value);
      setShowCreateTagModal(true);
    } else {
      const tags = selectedOptions || [];
      setEditingCliente((prev) => ({ ...prev, segments: tags }));
    }
  };

  // Función para mostrar el color picker al hacer clic en una etiqueta
  const handleEditColor = (tagData) => {
    setColorPickerTag(tagData);
  };

  // Actualiza el color de la etiqueta seleccionada mediante el SketchPicker
  const handleColorChangeComplete = (color) => {
    setEditingCliente((prev) => ({
      ...prev,
      segments: prev.segments.map((tag) =>
        tag.value === colorPickerTag.value ? { ...tag, color: color.hex } : tag
      ),
    }));
    setColorPickerTag(null);
  };

  // Al guardar, limpiamos las propiedades extra para enviar solo { value, label, color }
  const handleSave = () => {
    // Limpiamos el objeto cliente para enviar solo los campos editables
    const {
      nombre,
      apellido,
      email,
      telefono,
      fechaCumpleanos,
      genero,
      geographic_info,
      segments
    } = editingCliente;

    const cleanSegments = segments.map(({ value, label, color }) => ({ value, label, color }));

    const clienteToSave = {
      nombre,
      apellido,
      email,
      telefono,
      fechaCumpleanos,
      genero,
      geographic_info,
      segments: cleanSegments
    };

    onSave(clienteToSave);
  };

  const currentTags = editingCliente.segments;

  return (
    <div className="fixed z-50 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4 text-center sm:p-0">
        {/* Fondo oscuro */}
        <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={onClose}>
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        {/* Contenedor del modal */}
        <div className="bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all w-full max-w-md sm:max-w-lg lg:max-w-2xl mx-auto">
          <div className="px-6 pt-6 pb-4">
            {/* Header */}
            <div className="flex justify-between items-center pb-4 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Editar Cliente</h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Formulario */}
            <form className="mt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Campos básicos */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nombre</label>
                  <input
                    type="text"
                    name="nombre"
                    value={editingCliente.nombre}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    placeholder="Nombre del cliente"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Apellido</label>
                  <input
                    type="text"
                    name="apellido"
                    value={editingCliente.apellido}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    placeholder="Apellido del cliente"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={editingCliente.email}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    placeholder="email@ejemplo.com"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono</label>
                  <input
                    type="text"
                    name="telefono"
                    value={editingCliente.telefono}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    placeholder="+34 123 456 789"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fecha de Nacimiento</label>
                  <input
                    type="date"
                    name="fechaCumpleanos"
                    value={editingCliente.fechaCumpleanos}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Género</label>
                  <select
                    name="genero"
                    value={editingCliente.genero}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  >
                    <option value="HOMBRE">Hombre</option>
                    <option value="MUJER">Mujer</option>
                    <option value="NO_BINARIO">No binario</option>
                    <option value="PREFIERO_NO_DECIRLO">Prefiero no decirlo</option>
                  </select>
                </div>

                {/* Información geográfica */}
                <div className="md:col-span-2">
                  <h4 className="text-sm font-medium text-gray-700 mb-3 border-t pt-4">Información Geográfica</h4>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">País</label>
                  <input
                    type="text"
                    name="geographic_info.country"
                    value={editingCliente.geographic_info.country}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    placeholder="País"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Estado/Región</label>
                  <input
                    type="text"
                    name="geographic_info.state"
                    value={editingCliente.geographic_info.state}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    placeholder="Estado o región"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Ciudad</label>
                  <input
                    type="text"
                    name="geographic_info.city"
                    value={editingCliente.geographic_info.city}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    placeholder="Ciudad"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Código Postal</label>
                  <input
                    type="text"
                    name="geographic_info.postal_code"
                    value={editingCliente.geographic_info.postal_code}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    placeholder="Código postal"
                  />
                </div>

                {/* Campo de Etiquetas */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Etiquetas</label>
                  <CreatableSelect
                    isMulti
                    isClearable={false}
                    options={mergedTagOptions}
                    value={currentTags}
                    onChange={handleSegmentsChange}
                    components={{ MultiValue }}
                    styles={customSelectStyles}
                    placeholder="Selecciona o crea etiquetas..."
                    onCreateOption={(inputValue) => {
                      const exists = editingCliente.segments.some(
                        (tag) => tag.value.toLowerCase() === inputValue.toLowerCase()
                      );
                      if (exists) return;
                      setNewTagDefaultName(inputValue);
                      setShowCreateTagModal(true);
                    }}
                    onEditColor={handleEditColor}
                  />
                </div>
              </div>

              {/* Botones */}
              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                <button
                  className="px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                  onClick={onClose}
                >
                  Cancelar
                </button>
                <button
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium"
                  onClick={handleSave}
                >
                  Guardar Cambios
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Modales anidados */}
        {showCreateTagModal && (
          <CreateTagModal
            initialName={newTagDefaultName}
            onCreate={(newTag) => {
              const exists = editingCliente.segments.some(
                (tag) => tag.value.toLowerCase() === newTag.value.toLowerCase()
              );
              if (!exists) {
                setEditingCliente((prev) => ({ ...prev, segments: [...prev.segments, newTag] }));
              }
              setShowCreateTagModal(false);
            }}
            onClose={() => setShowCreateTagModal(false)}
          />
        )}

        {colorPickerTag && (
          <div className="fixed inset-0 z-60 flex items-center justify-center">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setColorPickerTag(null)}></div>
            <div className="relative z-70">
              <SketchPicker
                color={colorPickerTag.color}
                onChangeComplete={handleColorChangeComplete}
                className="shadow-2xl"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditClienteModal;
